import React, { createContext, useContext, useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi, userApi } from '@/services/api'
import type { UserProfile, LoginInput, RegisterInput } from '@/types'

interface AuthContextType {
  user: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (data: LoginInput) => Promise<void>
  register: (data: RegisterInput) => Promise<void>
  resendVerificationEmail: (email: string) => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (data: { token: string; password: string; passwordConfirm: string }) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    !!localStorage.getItem('auth_token')
  )
  
  const queryClient = useQueryClient()

  // Get current user query
  const { data: user, isLoading, refetch } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      try {
        const response = await userApi.getCurrentUser()
        return response.data.data.user
      } catch (error) {
        // If getCurrentUser fails, the user is not authenticated
        setIsAuthenticated(false)
        localStorage.removeItem('auth_token')
        throw error
      }
    },
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: async (response) => {
      const { user, token } = response.data.data
      localStorage.setItem('auth_token', token)
      setIsAuthenticated(true)
      
      // Set user data in cache and refetch to ensure consistency
      queryClient.setQueryData(['user', 'me'], user)
      
      // Trigger a refetch to ensure we have the latest user data
      setTimeout(() => {
        refetch()
      }, 100)
    },
    onError: (error) => {
      // Clear any existing auth state on login error
      localStorage.removeItem('auth_token')
      console.error('login error', error)
      setIsAuthenticated(false)
      queryClient.removeQueries({ queryKey: ['user', 'me'] })
    }
  })

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: (response) => {
      const { user } = response.data.data
      // After registration, user typically needs email verification
      // Don't set as authenticated yet, just cache the user data
      queryClient.setQueryData(['user', 'me'], user)
    },
  })

  const login = async (data: LoginInput) => {
    await loginMutation.mutateAsync(data)
  }

  const register = async (data: RegisterInput) => {
    await registerMutation.mutateAsync(data)
  }

  const resendVerificationEmail = async (email: string) => {
    await authApi.resendVerificationEmail(email)
  }

  const forgotPassword = async (email: string) => {
    await authApi.forgotPassword(email)
  }

  const resetPassword = async (data: { token: string; password: string; passwordConfirm: string }) => {
    await authApi.resetPassword(data)
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    setIsAuthenticated(false)
    queryClient.clear()
    authApi.logout().catch(() => {})
  }

  // Check token validity on mount and refetch user if needed
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (token && !isAuthenticated) {
      setIsAuthenticated(true)
    } else if (token && isAuthenticated && !user) {
      // If we have a token and are authenticated but no user data, refetch
      refetch()
    }
  }, [isAuthenticated, user, refetch])

  const value: AuthContextType = {
    user: user || null,
    isAuthenticated,
    isLoading: isLoading && isAuthenticated,
    login,
    register,
    resendVerificationEmail,
    forgotPassword,
    resetPassword,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 